import { Alert, Badge, Button, Group, Modal, Stack, Text, TextInput, Title } from "@mantine/core";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import type { WeeklyDetail } from "@msc/shared";

import {
  useCloseWeekly,
  useCreateProspect,
  useReopenWeekly,
  useSaveGeneralNotes,
  weeklyKeys,
} from "../../hooks/useWeekly.js";
import { useCurrentUser } from "../../hooks/useAuth.js";
import { useWeeklyLive } from "../../hooks/useWeeklyLive.js";
import { formatBerlinDate } from "../../lib/date.js";
import { datenstandBezugszeit, kwLabel, todosFuerZeile } from "../../lib/weekly.js";
import { ApiErrorAlert } from "../ApiErrorAlert.js";
import { BudgetSection } from "./BudgetSection.js";
import { LiveKontext } from "./LiveKontext.js";
import { LiveStatus } from "./LiveStatus.js";
import { NotizFeld } from "./NotizFeld.js";
import { RowCard } from "./RowCard.js";
import { TicketSummary } from "./TicketSummary.js";
import { TodoList } from "./TodoList.js";

export function WeeklyView({ detail }: { detail: WeeklyDetail }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { weekly, rows, todos, permissions, metrics, links } = detail;
  const woche = { isoYear: weekly.isoYear, isoWeek: weekly.isoWeek };
  const readOnly = !permissions.canWrite;
  const saveGeneral = useSaveGeneralNotes(weekly.id);
  const close = useCloseWeekly(weekly.id);
  const reopen = useReopenWeekly(weekly.id);
  const addProspect = useCreateProspect(weekly.id);
  const [prospectName, setProspectName] = useState("");
  const [closeOpen, setCloseOpen] = useState(false);
  const neuLaden = () => void qc.invalidateQueries({ queryKey: weeklyKeys.detail(weekly.id) });
  // Live-Raum nur für offene Weeklies (#305); abgeschlossene sind eingefroren.
  const live = useWeeklyLive(weekly.id, weekly.status === "offen");
  const { user } = useCurrentUser();

  // Datenstand-Hinweis: offen gegen „jetzt", abgeschlossen gegen den Stichtag der eingefrorenen Kennzahlen.
  const bezugszeit = datenstandBezugszeit(weekly, metrics);
  const gruppe = (kind: "customer" | "prospect" | "former") => rows.filter((r) => r.kind === kind);

  return (
    <LiveKontext.Provider value={live}>
    <Stack gap="lg">
      <Group justify="space-between">
        <Group gap="sm">
          <Title order={2}>{`${kwLabel(t, woche)} – ${weekly.leadName} · ${weekly.segmentName}`}</Title>
          <Badge color={weekly.status === "offen" ? "blue" : "gray"}>
            {weekly.status === "offen" ? t("weekly.statusOpen") : t("weekly.statusClosed")}
          </Badge>
          <LiveStatus eigeneEmployeeId={user?.employeeId ?? null} />
        </Group>
        <Group gap="xs">
          {permissions.canWrite && (
            <Button onClick={() => setCloseOpen(true)} loading={close.isPending}>
              {t("weekly.close")}
            </Button>
          )}
          {permissions.canReopen && (
            <Button variant="light" onClick={() => reopen.mutate(undefined)} loading={reopen.isPending}>
              {t("weekly.reopen")}
            </Button>
          )}
        </Group>
      </Group>

      {weekly.status === "abgeschlossen" && (
        <Alert color="gray">
          {t("weekly.closedBanner", {
            date: weekly.closedAt ? formatBerlinDate(weekly.closedAt) : "",
            name: weekly.closedByName ?? "",
          })}
        </Alert>
      )}
      {weekly.status === "offen" && readOnly && <Alert color="gray">{t("weekly.readOnly")}</Alert>}
      {reopen.error && <ApiErrorAlert error={reopen.error} />}

      {metrics ? (
        <>
          <BudgetSection
            budget={metrics.budget}
            sfSyncedAt={metrics.sfSyncedAt}
            salesforceRecordBaseUrl={links.salesforceRecordBaseUrl}
            bezugszeit={bezugszeit}
          />
          <TicketSummary
            stats={metrics.tickets.total}
            firstResponseMode={metrics.firstResponseMode}
            jiraBrowseBaseUrl={links.jiraBrowseBaseUrl}
            jiraSyncedAt={metrics.jiraSyncedAt}
            bezugszeit={bezugszeit}
          />
        </>
      ) : (
        weekly.status === "abgeschlossen" && <Alert color="gray">{t("weekly.metrics.notFrozen")}</Alert>
      )}

      <Stack gap="xs">
        <Title order={4}>{t("weekly.general")}</Title>
        <NotizFeld
          feld="general"
          label={t("weekly.generalNotes")}
          value={weekly.generalNotes}
          version={weekly.generalNotesVersion}
          readOnly={readOnly}
          onConflict={neuLaden}
          save={(v) => saveGeneral.mutateAsync(v)}
        />
        <TodoList weeklyId={weekly.id} todos={todosFuerZeile(todos, null)} woche={woche} readOnly={readOnly} />
      </Stack>

      <Stack gap="xs">
        <Title order={4}>{t("weekly.customers")}</Title>
        {gruppe("customer").map((r) => (
          <RowCard
            key={r.customerId}
            weeklyId={weekly.id}
            row={r}
            todos={todos}
            woche={woche}
            readOnly={readOnly}
            onConflict={neuLaden}
            metrics={metrics}
            links={links}
          />
        ))}
      </Stack>

      <Stack gap="xs">
        <Title order={4}>{t("weekly.prospects")}</Title>
        {gruppe("prospect").map((r) => (
          <RowCard
            key={r.prospectId}
            weeklyId={weekly.id}
            row={r}
            todos={todos}
            woche={woche}
            readOnly={readOnly}
            onConflict={neuLaden}
            metrics={metrics}
            links={links}
          />
        ))}
        {!readOnly && (
          <Group gap="xs">
            <TextInput
              placeholder={t("weekly.prospectName")}
              value={prospectName}
              onChange={(e) => setProspectName(e.currentTarget.value)}
            />
            <Button
              variant="light"
              disabled={!prospectName.trim()}
              loading={addProspect.isPending}
              onClick={() => addProspect.mutate(prospectName.trim(), { onSuccess: () => setProspectName("") })}
            >
              {t("weekly.addProspect")}
            </Button>
          </Group>
        )}
        {addProspect.error && <ApiErrorAlert error={addProspect.error} />}
      </Stack>

      {gruppe("former").length > 0 && (
        <Stack gap="xs">
          <Title order={4}>{t("weekly.former")}</Title>
          {gruppe("former").map((r) => (
            <RowCard
              key={r.customerId ?? r.prospectId}
              weeklyId={weekly.id}
              row={r}
              todos={todos}
              woche={woche}
              readOnly={readOnly}
              onConflict={neuLaden}
              metrics={metrics}
              links={links}
            />
          ))}
        </Stack>
      )}

      <Modal opened={closeOpen} onClose={() => setCloseOpen(false)} title={t("weekly.close")} centered>
        <Stack gap="md">
          <Text size="sm">{t("weekly.closeConfirm")}</Text>
          {close.error && <ApiErrorAlert error={close.error} />}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setCloseOpen(false)}>
              {t("common.cancel")}
            </Button>
            <Button
              loading={close.isPending}
              onClick={() => close.mutate(undefined, { onSuccess: () => setCloseOpen(false) })}
            >
              {t("weekly.close")}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
    </LiveKontext.Provider>
  );
}
